import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import AppToolBar from "../../../../common/AppToolBar";
import Header from "../../../../common/Header";
import {
  getEventDetailsWithDistkey,
  getFindBib,
} from "../../../../../apis/eventService";
import Snackbar from "../../../../common/Snackbar";
import httpService from "../../../../../apis/httpService";

const API_URL_FILE = httpService.API_URL();

const SearchBibUser = ({ classes }) => {
  const [eventDetails, setEventDetails] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [suggestionList, setSuggestionList] = useState([]);
  const [activeField, setActiveField] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputRect, setInputRect] = useState(null);
  const inputRef = useRef(null);

  // ---- Load event + participants ----
  useEffect(() => {
    const search = window.location.href;
    let distKey = search.split("distKey=")[1];
    if (distKey?.includes("&")) distKey = distKey.split("&")[0];
    if (distKey) handleDistKeyLoad(distKey);
  }, []);

  const handleDistKeyLoad = useCallback(async (distKey) => {
    try {
      const res = await getEventDetailsWithDistkey(distKey);
      if (res.status === 200) {
        setEventDetails(res.data);
        const requestParams = { includeVolunteer: false };
        const participants = await getFindBib(requestParams, res.data.id);
        if (participants?.data && participants?.data?.data?.length) {
          setSuggestionList(participants.data.data);
        }
      }
    } catch (err) {
      console.error("Error fetching event details:", err);
    }
  }, []);

  // ---- Handle Search ----
  const handleSearch = useCallback(async () => {
    setShowSuggestions(false);
    
    if (!eventDetails?.id) return;

    setLoading(true);
    const params = { includeVolunteer: false };
    if (name.trim()) params.name = name.trim();

    try {
      const res = await getFindBib(params, eventDetails.id);
      const list = res?.data?.data || [];
      if (list.length) setResults(list);
      else {
        setResults([]);
        Snackbar.show({
          variant: "info",
          isAutoHide: true,
          message: "No matching records found.",
        });
      }
    } catch (err) {
      Snackbar.show({
        variant: "error",
        isAutoHide: true,
        message: "Something went wrong while fetching results.",
      });
    } finally {
      setLoading(false);
    }
  }, [ eventDetails]);

  // ---- Suggestions ----
  const nameSuggestions = useMemo(() => {
    const q = name.trim().toLowerCase();
    if (!q || q.length <= 2) return [];
    if (!q || activeField !== "name") return [];
    return suggestionList
      .filter((p) => p.runner?.name?.toLowerCase().includes(q));
  }, [name, suggestionList, activeField]);

  // ---- Suggestion Selection ----
  const selectSuggestion = useCallback((participant) => {

   setResults([participant]);
    setName("");
    setShowSuggestions(false);
    setActiveField(null);
  }, []);

  // ---- Handle Focus/Blur ----
  const handleFocus = (field) => {
    setActiveField(field);
    setTimeout(() => setShowSuggestions(true), 50);
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  // ---- Track input position for portal ----
  const updateInputRect = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setInputRect(rect);
    }
  };

  useEffect(() => {
    updateInputRect();
    window.addEventListener("scroll", updateInputRect);
    window.addEventListener("resize", updateInputRect);
    return () => {
      window.removeEventListener("scroll", updateInputRect);
      window.removeEventListener("resize", updateInputRect);
    };
  }, []);

  return (
    <>
      <AppToolBar>
        <Header label="Find Your BIB" showDrawer />
      </AppToolBar>

      <div className={classes.ImageWrapper}>
        {eventDetails?.image && (
          <img
            src={`${API_URL_FILE}${eventDetails.image.url}`}
            alt="Event Banner"
            className={classes.bannerImg}
          />
        )}
      </div>

      <div className={classes.wrapper}>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <Typography className={classes.title}>
              {eventDetails?.name || "Event Details"}
            </Typography>
            <Typography className={classes.subtitle}>Find your BIB</Typography>

            {/* NAME FIELD */}
            <div style={{ position: "relative", width: "100%" }}>
              <TextField
                inputRef={inputRef}
                placeholder="Enter your full name"
                variant="outlined"
                fullWidth
                className={classes.input}
                value={name}
                onFocus={() => handleFocus("name")}
                onBlur={handleBlur}
                onChange={(e) => {
                  handleFocus("name");
                  setName(e.target.value);
                  updateInputRect();
                }}
                // onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            {/* Search Button */}
            {/* <div style={{ width: "100%", marginTop: "15px", marginBottom: "18px" }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                className={classes.searchBtn}
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Check BIB Number"
                )}
              </Button>
            </div> */}
          </CardContent>
        </Card>

        {/* ---- Portal Suggestion Dropdown ---- */}
        {showSuggestions && nameSuggestions.length > 0 && inputRect &&
          ReactDOM.createPortal(
            <div
              className={classes.suggestionBox}
              style={{
                position: "absolute",
                top: inputRect.bottom + window.scrollY,
                left: inputRect.left + window.scrollX,
                width: inputRect.width,
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 6,
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                zIndex: 9999,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {nameSuggestions.map((p) => (
                <div
                  key={p.id || `${p?.runner?.name}-${p.runner?.contactNumber}`}
                  className={classes.suggestionItem}
                  onClick={() => selectSuggestion(p)}
                  onMouseDown={() => selectSuggestion(p)}
                >
                  <div style={{ fontWeight: 700 }}>{p?.runner?.name}</div>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    {p?.eventRunCategory?.categoryName || ""}
                  </div>
                </div>
              ))}
            </div>,
            document.body
          )}

        {/* ---- Results ---- */}
        <Grid container spacing={2} className={classes.resultContainer}>
          <Grid item xs={12}>
            <Typography variant="h6" style={{ marginBottom: 8 }}>
              Search Results
            </Typography>

            {!loading && results.length === 0 && (
              <Typography style={{ color: "gray" }}>
                No results found.
              </Typography>
            )}

            {results.map((r) => (
              <Card
                key={r.id || r.bib}
                variant="outlined"
                style={{
                  textAlign: "center",
                  borderRadius: 8,
                  borderColor: "#ddd",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                  marginBottom: 12,
                }}
              >
                <CardContent style={{ alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    color="error"
                    style={{
                      border: "1px solid #f25c05",
                      padding: "4px 8px",
                      borderRadius: 6,
                      fontWeight: 600,
                      width: "fit-content",
                      margin: "auto",
                      marginBottom: 8,
                    }}
                  >
                    Your BIB Number
                  </Typography>

                  <Typography
                    variant="h3"
                    style={{
                      fontWeight: 800,
                      color: "#000",
                      padding: "10px 0",
                    }}
                  >
                    {r.bibNumber || "N/A"}
                  </Typography>

                  <Box>
                    <Typography variant="subtitle1">
                      Name:{" "}
                      <strong>{r.runner.name?.toUpperCase() || "-"}</strong>
                    </Typography>
                    <Typography variant="subtitle1">
                      {r?.eventRunCategory?.activityType?.displayName || "Step"}:{" "}
                      <strong>
                        {r?.eventRunCategory?.categoryName || "N/A"}
                      </strong>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Grid>
        </Grid>
      </div>
    </>
  );
};

const styles = {
  ImageWrapper: { maxWidth: "100%", margin: "59px auto 0" },
  wrapper: { maxWidth: 620, margin: "10px auto 0", padding: 14, overflow: "visible" },
  bannerImg: { width: "100%", height: "auto", marginBottom: 16 },
  card: { borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.1)", border: "1px solid #ccc" },
  cardContent: { textAlign: "center", padding: 32, overflow: "visible" },
  title: { fontWeight: 800, fontSize: 26 },
  subtitle: { fontWeight: 700, fontSize: 20, marginBottom: 20 },
  input: { borderRadius: 8, border: "1px solid #ccc" },
  searchBtn: {
    backgroundColor: "#0b63ff",
    color: "#fff",
    padding: "12px 16px",
    fontWeight: 700,
    borderRadius: 8,
  },
  suggestionItem: {
    padding: "10px 12px",
    cursor: "pointer",
    borderBottom: "1px solid #f3f3f3",
    textAlign: "left",
  },
  resultContainer: { marginTop: 15 },
};

export default withStyles(styles)(SearchBibUser);
